import React, { useMemo, useEffect, useState, useCallback } from 'react';
import {
    BookingStatus,
    getAdminCampaign,
    getAdminCampaigns,
    getAdminClients,
    getAdminInfluencers,
    getAdminManager,
    getAdminManagers,
    getAdminRetrieveInfluencer,
    CreateAndSendAdminInvoiceData,
    PatchAdminBookingData,
} from '@round/api';
import useAbortableEffect from '../../../hooks/useAbortableEffect';
import styles from './AdminBookings.module.css';
import useOnScroll from '../../../hooks/useOnScroll';
import AdminBookingsTable, { AdminBookingsTableRow } from './BookingsTable/AdminBookingsTable';
import { showNotification } from '../../../utils/showNotification';
import CreateAdminBookingModal from './CreateBooking/CreateAdminBookingModal';
import ButtonOld from '../../../ui/Button/Button';
import { useAdminBookings } from './useAdminBookings';
import { DropdownOptionWithSubLabel, OptionWithSubLabel, Select } from '@round/ui-kit';
import { useAdminInvoices } from './useAdminInvoices';
import { useSelectPaginatedOptions } from '../../../hooks/useSelectPaginatedOptions';
import useUrlState from '../../../hooks/useUrlState';
import Badge from '../../../ui/Badge/Badge';
import Checkbox from '../../../ui/DataEntry/Checkbox/Checkbox';
import cn from 'classnames';
import { GenericDropdownOption } from '../../../utils/utils.types';
import { ValueType } from 'react-select';
import { bookingsFilterStyles } from '../../booking/helpers';
import { BookingsTableOptionsProvider } from './BookingsTableOptionsContext/BookingsTableOptionsContext';
import { formatToIsoDateString } from '../../../utils/utits';
import DatePickerSelect from '../../../ui/DataEntry/DatePicker/DatePickerSelect/DatePickerSelect';
import { getAdminClient, getAdminClientUsers, getAdminClientUser } from '@round/api';
import ApproveToBillModal from '../../booking/ApproveToBillModal/ApproveToBillModal';
import Modal from '../../../ui-new/Modal/Modal';
import Button from '../../../ui-new/Button/Button';
import CreateAdminInvoiceModal from './CreateInvoice/CreateAdminInvoiceModal';

const AdminBookings = () => {
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [isApproveToBillModalOpen, setIsApproveToBillModalOpen] = useState(false);
    const [isConfirmApproveToBillModalOpen, setIsConfirmApproveToBillModalOpen] = useState(false);
    const [currentApproveToBillBooking, setCurrentApproveToBillBooking] = useState<AdminBookingsTableRow | null>(null);

    const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false);

    const [urlState, setUrlState] = useUrlState<{
        exclude_declined: boolean;
        booking_manager_id: number | undefined;
        campaign_id: number | undefined;
        influencer_id: number | undefined;
        client_id: number | undefined;
        booker_id: number | undefined;
        date_created_start: string | undefined;
        date_created_end: string | undefined;
        date_approved_start: string | undefined;
        date_approved_end: string | undefined;
    }>(
        {
            exclude_declined: true,
        },
        {
            exclude_declined: 'boolean',
            booking_manager_id: 'number',
            campaign_id: 'number',
            influencer_id: 'number',
            client_id: 'number',
            booker_id: 'number',
        }
    );

    const {
        isInitialized: isBookingsInitialized,
        init: initBookings,
        reset: resetBookings,
        ...bookingsDataProvider
    } = useAdminBookings({
        page_size: 25,
        excluded_statuses: urlState.exclude_declined ? 'declined' : undefined,
        booking_manager_id: urlState.booking_manager_id ? `${urlState.booking_manager_id}` : undefined,
        campaign_id: urlState.campaign_id ? `${urlState.campaign_id}` : undefined,
        client_id: urlState.client_id ? `${urlState.client_id}` : undefined,
        influencer_id: urlState.influencer_id ? `${urlState.influencer_id}` : undefined,
        booker_id: urlState.booker_id ? `${urlState.booker_id}` : undefined,
        date_created_start: urlState.date_created_start,
        date_created_end: urlState.date_created_end,
        date_approved_start: urlState.date_approved_start,
        date_approved_end: urlState.date_approved_end,
    });

    useAbortableEffect(
        (signal) => {
            if (!isBookingsInitialized) {
                initBookings({ signal }).catch(() => {
                    showNotification('Could not load data', 'error');
                });
            }
        },
        [isBookingsInitialized, initBookings]
    );

    const invoiceIds = useMemo(() => {
        return bookingsDataProvider.bookings
            .filter((booking) => booking.client_invoice_id)
            .map((booking) => booking.client_invoice_id)
            .filter((invoiceId): invoiceId is number => !!invoiceId);
    }, [bookingsDataProvider.bookings]);

    const {
        invoices,
        initiate,
        initiated,
        hasErrorLoadingData: hasErrorLoadingInvoices,
    } = useAdminInvoices(invoiceIds);

    useEffect(() => {
        if (!isBookingsInitialized || initiated) {
            return;
        }
        initiate();
    }, [initiate, isBookingsInitialized, initiated]);

    const bookingsTableRows: AdminBookingsTableRow[] = useMemo(
        () =>
            bookingsDataProvider.bookings.map((booking) => {
                const invoice = invoices.find((invoice) => invoice.id === booking.client_invoice_id);
                const influencer = bookingsDataProvider.influencers.find(
                    (influencer) => influencer.id === booking.influencer_id
                );
                return {
                    ...booking,
                    influencer,
                    xero_invoice: invoice,
                };
            }),
        [bookingsDataProvider.bookings, bookingsDataProvider.influencers, invoices]
    );

    const onBookingsBottomReached = useOnScroll(
        () => {
            if (
                !bookingsDataProvider.isLoading &&
                !bookingsDataProvider.hasErrorLoadingData &&
                bookingsDataProvider.hasNextPage
            ) {
                bookingsDataProvider.loadNextPage().catch(() => {
                    showNotification('Could not load data', 'error');
                });
            }
        },
        { threshold: 400 }
    );

    const selectedDatesCreated: [Date, Date] | null =
        urlState.date_created_start && urlState.date_created_end
            ? [new Date(urlState.date_created_start), new Date(urlState.date_created_end)]
            : null;

    const handleSetDateCreated = useCallback(
        (dates: Date[] | null) => {
            if (!dates) {
                setUrlState({ date_created_start: undefined, date_created_end: undefined });
                resetBookings();
                return;
            }

            const [startDate, endDate] = dates;
            setUrlState({
                date_created_start: formatToIsoDateString(startDate),
                date_created_end: formatToIsoDateString(endDate),
            });

            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const selectedDatesApproved: [Date, Date] | null =
        urlState.date_approved_start && urlState.date_approved_end
            ? [new Date(urlState.date_approved_start), new Date(urlState.date_approved_end)]
            : null;

    const handleSetDateApproved = useCallback(
        (dates: Date[] | null) => {
            if (!dates) {
                setUrlState({ date_approved_start: undefined, date_approved_end: undefined });
                resetBookings();
                return;
            }

            const [startDate, endDate] = dates;
            setUrlState({
                date_approved_start: formatToIsoDateString(startDate),
                date_approved_end: formatToIsoDateString(endDate),
            });

            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const [selectedManagerOption, setSelectedManagerOption] = useState<ValueType<
        GenericDropdownOption<number>,
        false
    > | null>(null);

    const { props: managerSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getAdminManagers
    >({
        mapToOption: (item) => ({ value: item.id, label: item.username }),
        fetchOptions: getAdminManagers,
    });

    const handleSetSelectedManagerOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false> | null) => {
            setUrlState({ booking_manager_id: option?.value });
            setSelectedManagerOption(option);

            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialManager = useCallback(async (managerId: number) => {
        try {
            const response = await getAdminManager(managerId);
            if (response.status !== 200) {
                showNotification('Could not load manager', 'error');
                return;
            }
            setSelectedManagerOption({
                value: response.data.id,
                label: response.data.username,
            });
        } catch {
            showNotification('Could not load manager', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.booking_manager_id || selectedManagerOption?.value === urlState.booking_manager_id) {
            return;
        }

        const managerOption = managerSelectProps.options.find((option) => option.value === urlState.booking_manager_id);

        if (!managerOption) {
            fetchInitialManager(urlState.booking_manager_id);
            return;
        }
        setSelectedManagerOption(managerOption);
    }, [fetchInitialManager, managerSelectProps.options, urlState.booking_manager_id, selectedManagerOption]);

    const [selectedCampaignOption, setSelectedCampaignOption] = useState<ValueType<
        DropdownOptionWithSubLabel<number>,
        false
    > | null>(null);

    const { props: campaignSelectProps } = useSelectPaginatedOptions<
        DropdownOptionWithSubLabel<number>,
        typeof getAdminCampaigns
    >({
        fetchOptions: getAdminCampaigns,
        mapToOption: (item) => ({
            value: item.id,
            label: item.name,
            subLabel: item.client_name,
        }),
    });

    const handleSetSelectedCampaignOption = useCallback(
        (option: ValueType<DropdownOptionWithSubLabel<number>, false> | null) => {
            setUrlState({ campaign_id: option?.value });
            setSelectedCampaignOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialCampaign = useCallback(async (campaignId: number) => {
        try {
            const response = await getAdminCampaign(campaignId);
            if (response.status !== 200) {
                showNotification('Could not load campaign', 'error');
                return;
            }
            setSelectedCampaignOption({
                value: response.data.id,
                label: response.data.name,
                subLabel: response.data.client_name,
            });
        } catch {
            showNotification('Could not load campaign', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.campaign_id || selectedCampaignOption?.value === urlState.campaign_id) {
            return;
        }

        const campaignOption = campaignSelectProps.options.find((option) => option.value === urlState.campaign_id);

        if (!campaignOption) {
            fetchInitialCampaign(urlState.campaign_id);
            return;
        }
        setSelectedCampaignOption(campaignOption);
    }, [fetchInitialCampaign, campaignSelectProps.options, urlState.campaign_id, selectedCampaignOption]);

    const { props: creatorSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getAdminInfluencers
    >({
        fetchOptions: getAdminInfluencers,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const [selectedCreatorOption, setSelectedCreatorOption] = useState<ValueType<
        GenericDropdownOption<number>,
        false
    > | null>(null);

    const handleSetSelectedCreatorOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false> | null) => {
            setUrlState({ influencer_id: option?.value });
            setSelectedCreatorOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialCreator = useCallback(async (creatorId: number) => {
        try {
            const response = await getAdminRetrieveInfluencer(creatorId);
            if (response.status !== 200) {
                showNotification('Could not load Creator', 'error');
                return;
            }
            setSelectedCreatorOption({
                value: creatorId,
                label: response.data.name,
            });
        } catch {
            showNotification('Could not load Creator', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.influencer_id || selectedCreatorOption?.value === urlState.influencer_id) {
            return;
        }

        const creatorOption = creatorSelectProps.options.find((option) => option.value === urlState.influencer_id);

        if (!creatorOption) {
            fetchInitialCreator(urlState.influencer_id);
            return;
        }
        setSelectedCreatorOption(creatorOption);
    }, [fetchInitialCreator, creatorSelectProps.options, urlState.influencer_id, selectedCreatorOption]);

    const { props: clientSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getAdminClients
    >({
        fetchOptions: getAdminClients,
        mapToOption: (item) => ({ value: item.id, label: item.name }),
    });

    const [selectedClientOption, setSelectedClientOption] = useState<ValueType<
        GenericDropdownOption<number>,
        false
    > | null>(null);

    const handleSetSelectedClientOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false> | null) => {
            setUrlState({ client_id: option?.value });
            setSelectedClientOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialClient = useCallback(async (clientId: number) => {
        try {
            const response = await getAdminClient(clientId);
            if (response.status !== 200) {
                showNotification('Could not load client', 'error');
                return;
            }
            setSelectedClientOption({
                value: response.data.id,
                label: response.data.name,
            });
        } catch {
            showNotification('Could not load client', 'error');
        }
    }, []);

    useEffect(() => {
        if (!urlState.client_id || selectedClientOption?.value === urlState.client_id) {
            return;
        }

        const clientOption = clientSelectProps.options.find((option) => option.value === urlState.client_id);

        if (!clientOption) {
            fetchInitialClient(urlState.client_id);
            return;
        }
        setSelectedClientOption(clientOption);
    }, [fetchInitialClient, clientSelectProps.options, urlState.client_id, selectedClientOption]);

    const { props: bookerSelectProps } = useSelectPaginatedOptions<
        GenericDropdownOption<number>,
        typeof getAdminClientUsers
    >({
        fetchOptions: getAdminClientUsers,
        mapToOption: (item) => ({ value: item.id, label: item.email }),
    });

    const [selectedBookerOption, setSelectedBookerOption] = useState<ValueType<
        GenericDropdownOption<number>,
        false
    > | null>(null);

    const handleSetSelectedBookerOption = useCallback(
        (option: ValueType<GenericDropdownOption<number>, false> | null) => {
            setUrlState({ booker_id: option?.value });
            setSelectedBookerOption(option);
            resetBookings();
        },
        [resetBookings, setUrlState]
    );

    const fetchInitialBooker = useCallback(async (bookerId: number, requestInit: RequestInit) => {
        try {
            const response = await getAdminClientUser(bookerId, requestInit);
            if (response.status !== 200) {
                showNotification('Could not load booker', 'error');
                return;
            }
            setSelectedBookerOption({
                value: response.data.id,
                label: response.data.email,
            });
        } catch (e) {
            if (e instanceof Error && e.name === 'AbortError') {
                return;
            }

            showNotification('Could not load booker', 'error');
        }
    }, []);

    useAbortableEffect(
        (signal) => {
            if (!urlState.booker_id || selectedBookerOption?.value === urlState.booker_id) {
                return;
            }

            const bookerOption = bookerSelectProps.options.find((option) => option.value === urlState.booker_id);

            if (!bookerOption) {
                fetchInitialBooker(urlState.booker_id, { signal });
                return;
            }
            setSelectedBookerOption(bookerOption);
        },
        [fetchInitialBooker, bookerSelectProps.options, urlState.booker_id, selectedBookerOption]
    );

    const onBookingCreated = async (bookingId: number) => {
        try {
            const result = await bookingsDataProvider.addBooking(bookingId);
            if (!result.success) {
                showNotification(result.reason || 'Could not load created booking', 'error');
                return;
            }
        } catch {
            showNotification('Could not load created booking', 'error');
        } finally {
            setIsCreateModalOpen(false);
        }
    };

    const handleEditBooking = async (bookingId: number, data: PatchAdminBookingData) => {
        try {
            const result = await bookingsDataProvider.editBooking(bookingId, data);
            if (!result.success) {
                showNotification(result.reason || 'Could not update booking', 'error');
                return;
            }

            showNotification('Updated', 'info');
        } catch (e) {
            showNotification((e as Error).message, 'error');
            throw e;
        }
    };

    const handleSetStatus = async (bookingId: number, status: BookingStatus) => {
        try {
            const result = await bookingsDataProvider.setStatus(bookingId, status);
            if (!result.success) {
                showNotification(result.reason || 'Could not update status', 'error');
                return;
            }

            showNotification('Status updated', 'info');
        } catch (e) {
            showNotification((e as Error).message, 'error');
            throw e;
        }
    };

    const onClickApproveToBill = (booking: AdminBookingsTableRow) => {
        setCurrentApproveToBillBooking(booking);
        setIsConfirmApproveToBillModalOpen(true);
    };

    const handleApproveToBill = async (bookingId: number | undefined) => {
        if (typeof bookingId !== 'number') {
            return;
        }
        try {
            const result = await bookingsDataProvider.approveToBill(bookingId, {
                create_invoice: false,
            });
            if (!result.success) {
                showNotification(result.reason || 'Could not perform operation', 'error');
                return;
            }

            showNotification('Approved to bill', 'info');
            setIsApproveToBillModalOpen(false);

            setCurrentApproveToBillBooking(null);
        } catch (e) {
            showNotification((e as Error).message, 'error');
        }
    };

    const handleApproveToBillWithDraftInvoice = async (bookingId: number | undefined, discount?: number) => {
        if (typeof bookingId !== 'number') {
            return;
        }
        try {
            const result = await bookingsDataProvider.approveToBill(bookingId, {
                create_invoice: true,
                discount,
            });
            if (!result.success) {
                showNotification(result.reason || 'Could not perform operation', 'error');
                return;
            }

            showNotification('Approved to bill and draft invoice created', 'info');
            setIsApproveToBillModalOpen(false);
            setIsCreateInvoiceModalOpen(false);

            setCurrentApproveToBillBooking(null);
        } catch (e) {
            showNotification((e as Error).message, 'error');
        }
    };

    const handleCreateAndSendInvoice = async (data: CreateAndSendAdminInvoiceData) => {
        try {
            const result = await bookingsDataProvider.createAndSendInvoice(data);
            if (!result.success) {
                showNotification(result.reason || 'Could not create invoice', 'error');
                return;
            }

            showNotification('Invoice created and sent', 'info');
            setIsCreateInvoiceModalOpen(false);
        } catch (e) {
            showNotification('Could not create invoice', 'error');
        }
    };

    return (
        <>
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.filtersWrapper}>
                        <Badge
                            className={cn(styles.filterBadge, styles.clickable)}
                            onClick={() => {
                                setUrlState({ exclude_declined: !urlState.exclude_declined });
                                resetBookings();
                            }}
                        >
                            <span>Exclude declined/cancelled</span>
                            <Checkbox
                                value={!!urlState.exclude_declined}
                                className={cn(styles.checkbox, {
                                    [styles.checkbox_checked]: !!urlState.exclude_declined,
                                })}
                            />
                        </Badge>
                        <Select
                            {...managerSelectProps}
                            placeholder="Filter by manager..."
                            value={selectedManagerOption}
                            onChange={(option) => {
                                handleSetSelectedManagerOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />
                        <Select
                            {...campaignSelectProps}
                            placeholder="Filter by campaign..."
                            value={selectedCampaignOption}
                            onChange={(option) => {
                                handleSetSelectedCampaignOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                            components={{
                                Option: OptionWithSubLabel,
                            }}
                        />
                        <Select
                            {...creatorSelectProps}
                            placeholder="Filter by creator..."
                            value={selectedCreatorOption}
                            onChange={(option) => {
                                handleSetSelectedCreatorOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />
                        <Select
                            {...clientSelectProps}
                            placeholder="Filter by client..."
                            value={selectedClientOption}
                            onChange={(option) => {
                                handleSetSelectedClientOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />
                        <Select
                            {...bookerSelectProps}
                            placeholder="Filter by booker..."
                            value={selectedBookerOption}
                            onChange={(option) => {
                                handleSetSelectedBookerOption(option);
                            }}
                            styles={bookingsFilterStyles}
                            isClearable
                        />
                        <div>
                            <span className={styles.filterLabel}>Date Created</span>
                            <DatePickerSelect
                                value={selectedDatesCreated}
                                onChange={handleSetDateCreated}
                                onClear={() => {
                                    handleSetDateCreated(null);
                                }}
                                selectRange
                                className={styles.datePickerFilter}
                            />
                        </div>
                        <div>
                            <span className={styles.filterLabel}>Date Approved</span>
                            <DatePickerSelect
                                value={selectedDatesApproved}
                                onChange={handleSetDateApproved}
                                onClear={() => {
                                    handleSetDateApproved(null);
                                }}
                                selectRange
                                className={styles.datePickerFilter}
                            />
                        </div>
                    </div>
                    <ButtonOld onClick={() => setIsCreateModalOpen(true)}>Add New Booking</ButtonOld>
                </div>

                <div className={styles.groupContainer}>
                    <div className={styles.tableScrollContainer} onScroll={onBookingsBottomReached}>
                        <BookingsTableOptionsProvider>
                            <AdminBookingsTable
                                className={styles.table}
                                data={bookingsTableRows}
                                totals={bookingsDataProvider.totals}
                                loading={
                                    bookingsDataProvider.isLoading ||
                                    (!isBookingsInitialized && !bookingsDataProvider.hasErrorLoadingData)
                                }
                                hasErrorLoadingInvoices={hasErrorLoadingInvoices}
                                updateBooking={handleEditBooking}
                                onStatusChange={handleSetStatus}
                                approveToBill={onClickApproveToBill}
                            />
                        </BookingsTableOptionsProvider>
                    </div>
                </div>
            </div>

            <CreateAdminBookingModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onCreated={onBookingCreated}
            />

            <Modal
                isOpen={isConfirmApproveToBillModalOpen}
                onClose={() => {
                    setCurrentApproveToBillBooking(null);
                    setIsConfirmApproveToBillModalOpen(false);
                }}
                closeOnOverlayClick
                closeOnEscape
            >
                <Modal.Header>Approve to Bill?</Modal.Header>

                <div>Are you sure you want to approve this booking to bill?</div>

                <Modal.Actions>
                    <Button
                        appearance="ghost"
                        onClick={() => {
                            setIsConfirmApproveToBillModalOpen(false);
                            setCurrentApproveToBillBooking(null);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        onClick={() => {
                            setIsConfirmApproveToBillModalOpen(false);
                            setIsApproveToBillModalOpen(true);
                        }}
                    >
                        Approve
                    </Button>
                </Modal.Actions>
            </Modal>

            <ApproveToBillModal
                isOpen={isApproveToBillModalOpen}
                onClose={() => {
                    setCurrentApproveToBillBooking(null);
                    setIsApproveToBillModalOpen(false);
                }}
                onApprove={() => handleApproveToBill(currentApproveToBillBooking?.id)}
                onCreateInvoice={() => {
                    setIsApproveToBillModalOpen(false);
                    setIsCreateInvoiceModalOpen(true);
                }}
            />

            <CreateAdminInvoiceModal
                data={
                    currentApproveToBillBooking
                        ? {
                              clientId: currentApproveToBillBooking.client_id,
                              bookingId: currentApproveToBillBooking.id,
                              bookingCost: currentApproveToBillBooking.price,
                              bookingCurrencyId: currentApproveToBillBooking.currency,
                          }
                        : null
                }
                onCreateAndSendInvoice={handleCreateAndSendInvoice}
                onSaveAsDraft={(discount) =>
                    handleApproveToBillWithDraftInvoice(currentApproveToBillBooking?.id, discount)
                }
                isOpen={isCreateInvoiceModalOpen}
                onClose={() => setIsCreateInvoiceModalOpen(false)}
            />
        </>
    );
};

export default AdminBookings;
